<template>
    <div
        class="ppv2-space-y-2"
        :class="{ isNotrequired: !required, 'floating-label-container': floatingLabel, classContainerAdd }"
        :style="wrapperStyle"
    >
        <BaseLabel
            :customSizeLabel="customSizeLabel"
            v-if="label && !floatingLabel"
            :label="label"
            :for-id="id"
            :required="required"
            :customClass="customLabelClass"
        />

        <div class="base-date-input-wrapper ppv2-relative" :class="{ 'has-floating-label': floatingLabel }">
            <label
                v-if="label && floatingLabel"
                class="bdi-floating-label ppv2-text-opacity-70 hover:ppv2-text-opacity-100"
                :class="customLabelClass"
                :style="floatingLabelStyle"
                :for="id"
            >
                {{ label }}
            </label>
            <DatePicker
                :default-value="defaultValueDate"
                :value="pickerValue"
                @input="onInput"
                type="date"
                :format="displayFormat"
                :value-type="'YYYY-MM-DD'"
                :disabled-date="isDateDisabled"
                :disabled="disabled"
                :placeholder="placeholder || 'JJ/MM/AAAA'"
                :lang="lang"
                :input-attr="inputAttrs"
                append-to-body
                popup-class="base-date-input-popup"
                :input-class="[
                    'ppv2-h-[40px] ppv2-text-[#A0AEC0] ppv2-w-full ppv2-border ppv2-border-solid ppv2-border-[#9c9af880] ppv2-rounded-xl ppv2-py-2 ppv2-px-4 ppv2-pr-10 focus:ppv2-outline-none focus:ppv2-border-2 ppv2-cursor-pointer',
                    inputClass,
                ]"
            />
            <span class="base-input-icon-overlay ppv2-pointer-events-none">
                <Calendar1 :size="17" color="#94A3B8" :strokeWidth="1.5" />
            </span>
        </div>

        <!-- Texte d’erreur -->
        <p v-if="errorText && computedState === 'error'" class="error-text">
            {{ Array.isArray(errorText) ? errorText[0] : errorText }}
        </p>
    </div>
</template>

<script>
import BaseLabel from './BaseLabel.vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { Calendar1 } from 'lucide-vue'
import normalizeSize from '../../utils/normalizeSize'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'BaseDateInput',
    components: { BaseLabel, DatePicker, Calendar1 },
    props: {
        id: {
            type: String,
            default: () => `base-date-input-${Math.random().toString(36).substr(2, 9)}`,
        },
        value: { type: [String, Date], default: null },
        displayFormat: { type: String, default: 'DD/MM/YYYY' },
        minDate: { type: [String, Date], default: null },
        maxDate: { type: [String, Date], default: null },
        disabled: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        placeholder: { type: String, default: '' },
        label: { type: String, default: '' },
        inputClass: { type: String, default: '' },
        customSizeLabel: { type: Number, default: 15 },
        // When no value, show this month/date in the calendar
        defaultValue: { type: [String, Date], default: null },
        errorText: { type: [String, Array], default: '' },
        state: { type: String, default: 'default' },
        floatingLabel: { type: Boolean, default: false },
        floatingLabelBg: { type: String, default: null },
        customLabelClass: { type: String, default: '' },
        classContainerAdd: { type: String, default: '' },
    },
    computed: {
        wrapperStyle() {
            if (!this.customSizeLabel) return {}
            return { fontSize: normalizeFontSize(this.customSizeLabel, true) }
        },
        floatingLabelStyle() {
            const styles = {}
            if (this.floatingLabel && this.floatingLabelBg) {
                styles.backgroundColor = this.floatingLabelBg
            }
            if (this.customSizeLabel) {
                styles.fontSize = normalizeSize(this.customSizeLabel, true)
            }
            return styles
        },
        inputAttrs() {
            const styles = []
            if (this.customSizeLabel) {
                styles.push(`font-size:${normalizeSize(this.customSizeLabel, true)};`)
                styles.push('font-weight:inherit;')
            }
            return styles.length > 0 ? { style: styles.join('') } : {}
        },
        computedState() {
            if (this.disabled || this.state === 'disabled') {
                return 'disabled'
            }
            if (this.state === 'default' && this.errorText) {
                if (Array.isArray(this.errorText) && this.errorText.length > 0) {
                    return 'error'
                }
                if (typeof this.errorText === 'string' && this.errorText.trim() !== '') {
                    return 'error'
                }
            }
            return this.state
        },
        pickerValue() {
            if (!this.value) return null
            if (typeof this.value === 'string' && this.value.match(/^\d{4}-\d{2}-\d{2}$/)) return this.value
            const d = new Date(this.value)
            return isNaN(d.getTime()) ? null : d.toISOString().split('T')[0]
        },
        defaultValueDate() {
            if (!this.defaultValue) return new Date()
            if (typeof this.defaultValue === 'string' && this.defaultValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return new Date(this.defaultValue)
            }
            return this.defaultValue instanceof Date ? this.defaultValue : new Date()
        },
        lang() {
            if (this.$i18n.locale === 'en') return 'en'
            return {
                formatLocale: {
                    firstDayOfWeek: 1,
                    months: [
                        'Janvier',
                        'Février',
                        'Mars',
                        'Avril',
                        'Mai',
                        'Juin',
                        'Juillet',
                        'Août',
                        'Septembre',
                        'Octobre',
                        'Novembre',
                        'Décembre',
                    ],
                    monthsShort: ['Jan.', 'Fév.', 'Mar.', 'Avr.', 'Mai', 'Juin', 'Jui.', 'Aoû.', 'Sep.', 'Oct.', 'Nov.', 'Déc.'],
                    weekdays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
                    weekdaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
                    weekdaysMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
                },
            }
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        onInput(value) {
            this.$emit('input', value)
        },
        // `new Date('YYYY-MM-DD')` est parsé en UTC alors que les cellules du calendrier
        // sont construites en heure locale (`new Date(y, m, d)`) : sur un fuseau décalé, la
        // borne glissait d'un jour et laissait cliquable une date que le serveur refuse.
        // On reconstruit donc la borne en local, et on CLONE quand la prop est un Date
        // (setHours mutait l'objet du parent).
        toLocalStartOfDay(value) {
            if (!value) return null
            let d
            if (typeof value === 'string') {
                const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
                d = parts ? new Date(+parts[1], +parts[2] - 1, +parts[3]) : new Date(value)
            } else {
                d = new Date(value.getTime())
            }
            if (isNaN(d.getTime())) return null
            d.setHours(0, 0, 0, 0)
            return d
        },
        isDateDisabled(date) {
            const min = this.toLocalStartOfDay(this.minDate)
            if (min && date < min) return true

            const max = this.toLocalStartOfDay(this.maxDate)
            if (max) {
                max.setHours(23, 59, 59, 999)
                if (date > max) return true
            }
            return false
        },
    },
}
</script>

<style>
/* ── Wrapper & input ── */
.base-date-input-wrapper .mx-icon-calendar,
.base-date-input-wrapper .mx-icon-clear {
    display: none !important;
}

.base-date-input-wrapper .mx-datepicker {
    width: 100%;
}
.base-date-input-wrapper .mx-input {
    border-radius: 12px;
    font-size: inherit;
    font-family: inherit;
    cursor: pointer;
    box-shadow: none;
    background: transparent;
}

.error-text {
    color: #ed4b9e;
    font-size: clamp(10px, 0.625vw, 22px);
    margin-top: 4px;
}

.base-date-input-wrapper .mx-input.error {
    border-color: #ed4b9e !important;
}

/* Custom icon overlay */
.base-input-icon-overlay {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
}

/* ── Popup shell ──
   DO NOT use overflow:hidden here — it clips the header text and bottom rows.
   Apply border-radius to inner children instead. */
.base-date-input-popup.mx-datepicker-popup {
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    border: 1px solid #e2e8f0;
    margin-top: 4px;
    overflow: hidden;
    height: auto !important;
    min-height: max-content;
}
.base-date-input-popup .mx-calendar {
    font-family: 'Poppins', sans-serif;
    border: none;
    padding: 0;
    background: #ffffff;
    border-radius: 5px; /* rounded corners on the calendar itself */
    height: auto !important;
}

/* ── Calendar header (month/year nav) ── */
.base-date-input-popup .mx-calendar-header {
    background: linear-gradient(135deg, #021832 0%, #0f3460 100%);
    padding: 14px 14px;
    border-bottom: none;
    margin: 0;
    display: flex;
    align-items: center;
    min-height: 48px;
}
.base-date-input-popup .mx-calendar-header .mx-btn {
    color: #94a3b8;
    font-family: 'Poppins', sans-serif;
    font-weight: 700;
    font-size: clamp(12px, 0.729545vw, 16px);
    border-radius: 8px;
    padding: 4px 8px;
    transition: all 0.15s;
    line-height: 1.2;
}
.base-date-input-popup .mx-calendar-header .mx-btn:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
}
/* Hide year jump buttons (« ») */
.base-date-input-popup .mx-calendar-header .mx-btn-icon-double-left,
.base-date-input-popup .mx-calendar-header .mx-btn-icon-double-right {
    display: none !important;
}
.base-date-input-popup .mx-calendar-header .mx-btn-icon-left,
.base-date-input-popup .mx-calendar-header .mx-btn-icon-right {
    color: rgba(255, 255, 255, 0.5);
}
.base-date-input-popup .mx-calendar-header .mx-btn-icon-left:hover,
.base-date-input-popup .mx-calendar-header .mx-btn-icon-right:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
}

/* ── Calendar body content ── */
.base-date-input-popup .mx-calendar-content {
    padding: 8px 12px 16px;
    background: #ffffff;
    height: auto !important;
}

/* ── Weekday headers ── */
.base-date-input-popup .mx-table th {
    color: #94a3b8;
    font-size: 11px;
    font-weight: 700;
    font-family: 'Poppins', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 8px 0;
}
/* Weekend columns (Sa + Di) */
.base-date-input-popup .mx-table th:last-child,
.base-date-input-popup .mx-table th:nth-last-child(2) {
    color: #7d2dbd;
}

/* ── Day cells ── */
.base-date-input-popup .mx-table-date td {
    padding: 2px;
}
.base-date-input-popup .mx-calendar-content .cell {
    border-radius: 10px;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #334155 !important;
    transition:
        background 0.12s,
        color 0.12s;
    height: 34px;
    line-height: 34px;
}
.base-date-input-popup .mx-calendar-content .cell:hover {
    background: #f1f5f9;
    color: #021832 !important;
}

/* Selected */
.base-date-input-popup .mx-calendar-content .cell.active {
    background: #7d2dbd !important;
    color: #fff !important;
    font-weight: 700;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(125, 45, 189, 0.25);
}

/* Today indicator */
.base-date-input-popup .mx-table-date .today {
    color: #7d2dbd !important;
    font-weight: 700;
    position: relative;
}
.base-date-input-popup .mx-table-date .today:not(.active)::after {
    content: '';
    position: absolute;
    bottom: 3px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #7d2dbd;
}

/* Not current month */
.base-date-input-popup .mx-calendar-content .cell.not-current-month {
    color: #cbd5e1 !important;
    font-weight: 400;
}

/* Disabled */
.base-date-input-popup .mx-calendar-content .cell.disabled {
    color: #e2e8f0 !important;
    cursor: not-allowed;
    background: transparent !important;
}

/* ── DARK MODE ── */
.dark .base-date-input-wrapper .mx-input {
    background: transparent;
    color: #94a3b8;
    border-color: #9c9af880;
}
.dark .base-date-input-popup.mx-datepicker-popup {
    border-color: #334155;
}
.dark .base-date-input-popup .mx-calendar {
    background: #060b28;
}
.dark .base-date-input-popup .mx-calendar-content {
    background: #060b28;
}
.dark .base-date-input-popup .mx-table th {
    color: #475569;
}
.dark .base-date-input-popup .mx-table th:last-child,
.dark .base-date-input-popup .mx-table th:nth-last-child(2) {
    color: #a855f7;
}
.dark .base-date-input-popup .mx-calendar-content .cell {
    color: #94a3b8 !important;
}
.dark .base-date-input-popup .mx-calendar-content .cell:hover {
    background: #334155;
    color: #f1f5f9 !important;
}
.dark .base-date-input-popup .mx-calendar-content .cell.active {
    background: #7d2dbd !important;
    box-shadow: 0 2px 8px rgba(168, 85, 247, 0.35);
    color: #fff !important;
}
.dark .base-date-input-popup .mx-table-date .today {
    color: #a855f7 !important;
}
.dark .base-date-input-popup .mx-table-date .today:not(.active)::after {
    background: #a855f7;
}
.dark .base-date-input-popup .mx-calendar-content .cell.not-current-month {
    color: #475569 !important;
}
.dark .base-date-input-popup .mx-calendar-content .cell.disabled {
    color: #334155 !important;
}

/* ── Floating Label ── */
.floating-label-container {
    position: relative;
}

.bdi-floating-label {
    position: absolute;
    top: 0;
    transform: translateY(-65%);
    left: 12px;
    padding: 0 4px;
    z-index: 10;
    background-color: #fff;
    pointer-events: none;
    line-height: 1;
    font-size: inherit;
    opacity: 1 !important;
    white-space: nowrap;
}

.dark .bdi-floating-label {
    background-color: #060b28;
}

.base-date-input-wrapper.has-floating-label {
    margin-top: 8px;
}
</style>
